import { combinedRoles } from "./constants";
import { ApplicantType } from "shared/types";
import {
  ActivityItem,
  NoteData,
  ApplicantData,
  SplitApplicationActivity
} from "./types";
import { ApplicantRoleLabel, capitalize, formatToCurrency } from "shared/utils";
import { useEffect, useRef } from "react";
import { getFullName } from "shared/utils/get-full-name";

export const getAuthorText = ({
  authorName,
  authorRole
}: {
  authorName?: string;
  authorRole?: string | ApplicantType;
}) => {
  if (!authorName && !authorRole) return "";

  const readableRole = combinedRoles[authorRole || ""];

  if (authorName && readableRole) return `by ${authorName}, ${readableRole}`;

  return `by ${authorName || readableRole}`;
};

export const getActivityMessage = (item: ActivityItem): string | null => {
  if (!item.details || typeof item.details !== "object") return null;

  let nameEmail = "";
  if ("contact" in item.details) {
    const contact = item.details.contact;
    const email = contact?.email ? `(${contact.email})` : "";
    nameEmail = contact
      ? `${contact.first_name} ${contact.last_name} ${email}`
      : "";
  }

  switch (item.activity_type) {
    case "canadian_criminal_report_invited":
      return `${nameEmail} was emailed a Canadian criminal background check link`;
    case "decision_email_sent":
      return `${nameEmail} was emailed a decision letter`;
    case "credit_report_sent":
      return `${nameEmail} was emailed a credit report`;
    case "placed_on_hold":
    case "resumed":
      return [
        `by ${item.author?.name || "Boom Admin"}`,
        item.author?.author_role
          ? `(${capitalize(
              item.author.author_role.toLowerCase().replace("_", " ")
            )})`
          : ""
      ].join(" ");
    case "submitted":
      if (item.details?.application_fee)
        return `Base application fee (excl. discounts): ${formatToCurrency(
          item.details.application_fee
        )}`;
      return null;
    default:
      return null;
  }
};

export const extractEntitiesFromJson = (json: any): NoteData["entities"] => {
  const entities: NoteData["entities"] = [];

  const traverseContent = (content: any[]) => {
    if (!Array.isArray(content)) return;

    content.forEach((item) => {
      if (item.type === "mention" && item.attrs)
        entities.push({
          kind: "mention",
          subject_type: item.attrs.entity_name,
          subject_id: item.attrs.entity_identifier
        });

      if (item.content) traverseContent(item.content);
    });
  };

  if (json?.content) traverseContent(json.content);

  return entities;
};

export const isTextEmpty = (text: string | undefined): boolean => {
  if (!text) return true;
  return text.trim() === "" || text === "<p></p>";
};

export const isUnread = (activityItem: ActivityItem, currentUserId: number) => {
  if (!("rich_text" in activityItem)) return false;

  if (currentUserId === activityItem.author?.id) return false;

  const mentionedNotification = activityItem.rich_text?.notifications?.find(
    (ki) =>
      ki.kind === "mention" && String(ki.user.id) === String(currentUserId)
  );

  if (mentionedNotification?.read_at !== null) return false;

  return (
    String(mentionedNotification.user.id) !== String(activityItem.author?.id)
  );
};

export const getMentionNotificationId = (
  activityItem: ActivityItem,
  currentUserId: number
) => {
  if (!("rich_text" in activityItem)) return undefined;

  const mentionedNotification = activityItem.rich_text?.notifications?.find(
    (ki) =>
      ki.kind === "mention" && String(ki.user.id) === String(currentUserId)
  );

  if (!mentionedNotification) return undefined;

  return mentionedNotification.id || undefined;
};

export const useMarkRead = (
  items: { id: string; read: boolean }[],
  getElById: (id: string) => HTMLElement | null,
  markReadBatch: (ids: string[]) => Promise<void>,
  opts = { threshold: 0.5, dwellMs: 0, flushMs: 300 }
) => {
  const seen = useRef<Set<string>>(new Set());
  const queue = useRef<Set<string>>(new Set());
  const timers = useRef<Map<string, number>>(new Map());
  const flushTimer = useRef<number | null>(null);
  const ob = useRef<IntersectionObserver | null>(null);

  const scheduleFlush = () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(async () => {
      const ids = [...queue.current];
      if (!ids.length) return;
      queue.current.clear();
      await markReadBatch(ids);
    }, opts.flushMs) as unknown as number;
  };

  useEffect(() => {
    ob.current?.disconnect();
    ob.current = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const el = e.target as HTMLElement;
          const id = el.dataset.activityId ?? el.dataset.id ?? "";

          if (!id || seen.current.has(id)) continue;
          const visible =
            e.isIntersecting && e.intersectionRatio >= opts.threshold;
          if (visible) {
            if (opts.dwellMs && !timers.current.has(id)) {
              const t = window.setTimeout(() => {
                timers.current.delete(id);
                seen.current.add(id);
                queue.current.add(id);
                scheduleFlush();
                ob.current?.unobserve(el);
              }, opts.dwellMs) as unknown as number;
              timers.current.set(id, t);
            } else if (!opts.dwellMs) {
              seen.current.add(id);
              queue.current.add(id);
              scheduleFlush();
              ob.current?.unobserve(el);
            }
          } else {
            const t = timers.current.get(id);
            if (t) {
              clearTimeout(t);
              timers.current.delete(id);
            }
          }
        }
      },
      { threshold: [opts.threshold] }
    );

    for (const it of items) {
      if (it.read) {
        seen.current.add(it.id);
        continue;
      }
      const el = getElById(it.id);
      if (el) ob.current.observe(el);
    }

    return () => {
      ob.current?.disconnect();
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
      if (flushTimer.current) clearTimeout(flushTimer.current);
    };
  }, [items, opts.threshold, opts.dwellMs, opts.flushMs]);
};

export const getOriginalApplicantsText = (
  application: SplitApplicationActivity
) => {
  const remainingApplicants = application.details.remaining_applicants;
  const movingApplicants = application.details.moving_applicants;
  return getApplicantsText([...remainingApplicants, ...movingApplicants]);
};

export const getApplicantsText = (applicants?: ApplicantData[]) => {
  if (applicants && applicants.length)
    return applicants
      .map((applicant) => {
        const role = ApplicantRoleLabel[applicant.role]
          ? `(${ApplicantRoleLabel[applicant.role]})`
          : "";

        if ("first_name" in applicant)
          return `${getFullName({
            firstName: applicant.first_name,
            lastName: applicant.last_name,
            middleName: applicant.middle_name
          })} ${role}`;
        else return `${applicant.email} ${role}`;
      })
      .join(", ");
  return "";
};
